// Qué pasa con las TOOL CARDS cuando un chat se RESTAURA — recarga de la pestaña o abrir
// uno del historial. Puro, y con tests, porque acá una regla que falte se cobra en
// créditos (ADR-2026-09-08-el-autogenerado-no-sobrevive-a-la-recarga).

import type { V2ToolCard, V2ToolCardKind } from './useV2Chat';

/**
 * Cards que viven INLINE en el stream aunque estén pendientes. Hay DOS motivos distintos
 * para estar en esta lista, y conviene no mezclarlos al leerla.
 *
 * 1. PROPUESTAS de generación: se leen como un mensaje más del chat y funcionan SOLAS
 *    (generan sin necesitar el turno del agente vivo).
 */
const PROPUESTAS: V2ToolCardKind[] = [
  'generate_image',
  'generate_video',
  'generate_audio',
  // Las dos del lote también son propuestas autónomas: generan / crean por su cuenta,
  // con o sin el turno del agente vivo.
  'generar_lote_imagenes',
  'agregar_productos',
  // Llevar un video al Estudio también funciona sola: crea el proyecto y navega. Fuera de
  // esta lista, mandar otro mensaje la soltaba (`detachTurn`) y quedaba pendiente pero
  // invisible, sin el botón (review #171, ADR-2026-10-12-review-171-estudio-y-saldo).
  'ir_al_estudio',
];

/**
 * 2. Cards de SOLO MOSTRAR, que se auto-resuelven apenas montan. Sin montar no se
 *    resuelven, y el turno se queda esperando para siempre — el famoso "se quedó
 *    pensando" (ADR-2026-09-08-las-cards-de-mostrar-tienen-que-montar).
 *
 *    OJO con leer esta lista como "lo que se puede ver en el Chat": lo que NO está acá
 *    va al slot de card activa, que el Chat ahora también tiene
 *    (ADR-2026-09-09-el-chat-necesita-su-slot-de-card). Estas están acá porque son un
 *    mensaje más del chat —una galería, una descarga— y no algo que el turno esté
 *    esperando que contestes.
 */
const SOLO_MOSTRAR: V2ToolCardKind[] = [
  'show_recent_media',
  'mostrar_guardado',
  'contact_support',
  // La planilla y el PDF ya están armados cuando la card monta: sólo queda la descarga.
  'crear_planilla',
  'crear_pdf',
];

export const INLINE_PENDING_KINDS: V2ToolCardKind[] = [...PROPUESTAS, ...SOLO_MOSTRAR];

export const isInlinePendingCard = (c: { kind: V2ToolCardKind }): boolean =>
  INLINE_PENDING_KINDS.includes(c.kind);

/**
 * Las tool cards de un chat restaurado. Dos cosas, y la segunda cuesta plata si falta:
 *
 * 1. Las que quedaron 'pending' no tienen resolver tras restaurar (las funciones no se
 *    serializan). Las propuestas de generación sobreviven como `detached`: siguen
 *    pendientes y clickeables, pero no cuentan como card activa, así que no traban nada.
 *    Las demás pasan a 'cancelled': sin turno vivo no pueden hacer nada.
 *
 * 2. Se les BORRA el `autogenerate`. Es la intención de UN turno vivo ("el usuario tiene
 *    los permisos en directo, mandale"), no una propiedad de la card. Si sobrevive a la
 *    persistencia, cada recarga vuelve a disparar el último lote —una tanda entera de
 *    créditos por F5— y vuelve a crear los mismos productos. Restaurada, la card espera
 *    el click, que es lo único honesto: el turno que decidió generar solo ya no existe.
 */
export function restaurarToolCards(
  cards: V2ToolCard[] | undefined,
): V2ToolCard[] | undefined {
  return cards?.map((c) => {
    const tieneFlag =
      !!c.input &&
      typeof c.input === 'object' &&
      (c.input as { autogenerate?: unknown }).autogenerate === true;
    const input = tieneFlag ? { ...(c.input as object), autogenerate: false } : c.input;
    if (c.status !== 'pending') return { ...c, input };
    return isInlinePendingCard(c)
      ? { ...c, input, detached: true }
      : { ...c, input, status: 'cancelled' as const };
  });
}
